<template>
  <div class="role-manage">
    <div class="toolbar">
      <el-button type="primary" icon="el-icon-plus" size="small" @click="openAddDialog">新增角色</el-button>
      <el-button icon="el-icon-refresh" size="small" :loading="loading" @click="fetchData">刷新</el-button>
    </div>

    <el-table :data="roles" size="small" v-loading="loading" border>
      <el-table-column prop="name" label="角色名称" min-width="150">
        <template slot-scope="scope">
          <span>{{ scope.row.name }}</span>
          <el-tag v-if="scope.row.isSystem" class="role-flag" size="mini" type="info" effect="plain">系统角色</el-tag>
          <el-tag v-if="scope.row.locked" class="role-flag" size="mini" type="danger" effect="plain">锁定</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="code" label="角色编码" min-width="150"></el-table-column>
      <el-table-column prop="description" label="描述" min-width="220" show-overflow-tooltip></el-table-column>
      <el-table-column label="权限数量" width="100" align="center">
        <template slot-scope="scope">{{ permissionCount(scope.row) }}</template>
      </el-table-column>
      <el-table-column label="操作" :width="isMobile ? 64 : 170" fixed="right">
        <template slot-scope="scope">
          <div class="table-actions">
            <el-button type="text" size="mini" @click="openEditDialog(scope.row)">编辑</el-button>
            <el-button
              v-if="!scope.row.isSystem && !scope.row.locked"
              type="text"
              size="mini"
              class="danger-action"
              @click="deleteRole(scope.row)"
            >删除</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      :title="dialogMode === 'add' ? '新增角色' : '编辑角色'"
      :visible.sync="dialogVisible"
      width="720px"
      :close-on-click-modal="false"
    >
      <el-form ref="form" :model="form" label-width="90px" size="small">
        <el-form-item label="角色名称" required>
          <el-input v-model.trim="form.name" maxlength="60" placeholder="请输入角色名称"></el-input>
        </el-form-item>
        <el-form-item label="角色编码" required>
          <el-input
            v-model.trim="form.code"
            maxlength="60"
            placeholder="例如 event_operator"
            :disabled="dialogMode === 'edit'"
          ></el-input>
        </el-form-item>
        <el-form-item label="角色描述">
          <el-input v-model.trim="form.description" maxlength="200" placeholder="请输入角色描述"></el-input>
        </el-form-item>
        <el-form-item label="角色属性" v-if="dialogMode === 'edit'">
          <el-tag v-if="form.isSystem" size="small" type="info" effect="plain">系统角色</el-tag>
          <el-tag v-if="form.locked" size="small" type="danger" effect="plain">锁定角色</el-tag>
          <span v-if="form.locked" class="field-tip">锁定角色不允许修改权限</span>
        </el-form-item>
        <el-form-item label="权限配置">
          <el-alert
            v-if="form.permissions.includes('*')"
            title="该角色拥有全部权限"
            type="info"
            :closable="false"
            show-icon
            class="all-permission-alert"
          ></el-alert>
          <div class="permission-panel" :class="{ readonly: form.locked }">
            <div v-for="group in permissionGroups" :key="group.group" class="permission-group">
              <div class="permission-group-title">{{ group.group }}</div>
              <el-checkbox-group v-model="form.permissions" :disabled="form.locked">
                <el-checkbox
                  v-for="permission in group.permissions"
                  :key="permission.code"
                  :label="permission.code"
                >{{ permission.name }}</el-checkbox>
              </el-checkbox-group>
            </div>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="saving" @click="saveRole">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getToken } from '@/utils/auth.js';

const emptyForm = () => ({
  id: '',
  name: '',
  code: '',
  description: '',
  permissions: [],
  isSystem: false,
  locked: false
});

export default {
  name: 'RoleManage',
  data() {
    return {
      loading: false,
      saving: false,
      roles: [],
      permissionGroups: [],
      dialogVisible: false,
      dialogMode: 'add',
      form: emptyForm(),
      isMobile: false
    };
  },
  created() {
    this.fetchData();
  },
  mounted() {
    this.updateViewport();
    window.addEventListener('resize', this.updateViewport);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewport);
  },
  methods: {
    authHeaders() {
      return {
        'x-auth-token': getToken()
      };
    },
    updateViewport() {
      this.isMobile = window.innerWidth <= 768;
    },
    fetchData() {
      this.loading = true;
      Promise.all([
        this.$axios.get('/permissions', { headers: this.authHeaders() }),
        this.$axios.get('/roles', { headers: this.authHeaders() })
      ]).then(([permissionsRes, rolesRes]) => {
        this.permissionGroups = permissionsRes.data.res || [];
        this.roles = rolesRes.data.res || [];
      }).catch((err) => {
        this.$message.error(err.response?.data?.message || '角色数据加载失败');
      }).finally(() => {
        this.loading = false;
      });
    },
    permissionCount(role) {
      if (role.permissions && role.permissions.includes('*')) return '全部';
      return (role.permissions || []).length;
    },
    openAddDialog() {
      this.dialogMode = 'add';
      this.form = emptyForm();
      this.dialogVisible = true;
    },
    openEditDialog(role) {
      this.dialogMode = 'edit';
      this.form = {
        id: role._id,
        name: role.name,
        code: role.code,
        description: role.description || '',
        permissions: [...(role.permissions || [])],
        isSystem: !!role.isSystem,
        locked: !!role.locked
      };
      this.dialogVisible = true;
    },
    saveRole() {
      if (!this.form.name || !this.form.code) {
        this.$message.error('角色名称和编码不能为空');
        return;
      }
      this.saving = true;
      const payload = {
        id: this.form.id,
        name: this.form.name,
        code: this.form.code,
        description: this.form.description,
        permissions: this.form.permissions
      };
      const url = this.dialogMode === 'add' ? '/roles/add' : '/roles/update';
      this.$axios.post(url, JSON.stringify(payload), {
        headers: this.authHeaders()
      }).then(() => {
        this.$message.success('保存成功');
        this.dialogVisible = false;
        this.fetchData();
      }).catch((err) => {
        this.$message.error(err.response?.data?.message || '保存失败');
      }).finally(() => {
        this.saving = false;
      });
    },
    deleteRole(role) {
      this.$confirm(`确认删除角色「${role.name}」？`, '删除确认', {
        type: 'warning'
      }).then(() => {
        return this.$axios.post('/roles/delete', JSON.stringify({ id: role._id }), {
          headers: this.authHeaders()
        });
      }).then(() => {
        this.$message.success('删除成功');
        this.fetchData();
      }).catch((err) => {
        if (err === 'cancel') return;
        this.$message.error(err.response?.data?.message || '删除失败');
      });
    }
  }
};
</script>

<style scoped>
.role-manage {
  width: 100%;
}

.toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.danger-action {
  color: #f56c6c;
}

.table-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.table-actions .el-button {
  margin: 0;
}

.role-flag {
  margin-left: 8px;
  vertical-align: middle;
}

.permission-panel {
  max-height: 360px;
  overflow: auto;
  border: 1px solid var(--border-lighter);
  border-radius: 6px;
  padding: 12px 14px 2px;
}

.all-permission-alert {
  margin-bottom: 10px;
}

.permission-panel.readonly {
  background: var(--bg-page);
}

.permission-group {
  margin-bottom: 14px;
}

.permission-group-title {
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
}

.permission-group /deep/ .el-checkbox {
  margin-right: 18px;
  margin-bottom: 8px;
}

.field-tip {
  margin-left: 8px;
  color: var(--text-secondary);
  font-size: 12px;
}

@media (max-width: 768px) {
  .table-actions {
    flex-direction: column;
    align-items: center;
  }
}
</style>
